import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../../firebase/config";
import {
  FRESH_FIND_STATUS_OPTIONS,
  getProductAvailabilityLabel,
  getProductAvailabilityStatus,
} from "../../constants/availability";
import {
  formatAvailability,
  formatProductPrice,
} from "../../utils/productDisplay";
import {
  getSafeDistanceMiles,
  normalizeLocationRecord,
} from "../../utils/locationUtils";
import {
  getProfilePath,
  getStorefrontReadiness,
  getStorefrontUrl,
} from "../../utils/storefront";
import {
  getHomeStandLocationLabel,
  getHomeStandName,
  getHomeStandStatus,
  isHomeFreshFindsOptedIn,
  isHomePickupSpotDiscoverable,
  prepareHomeFarmStandDiscoveryItems,
  standCanShowHomeDirections,
} from "../../utils/homeFarmStandDiscovery";
import {
  canShowStandDirections,
  getStandDirectionsUrl,
} from "../../utils/standLocation";
import SectionCarousel from "../Common/SectionCarousel";
import SupportLink from "../Common/SupportLink";
import InstallAppButton from "../Common/InstallAppButton";
import JobFeed from "./JobFeed";
import CommunityAlertBar from "../Community/CommunityAlertBar";
import {
  calculateProfileCompletion,
  trackAppEvent,
} from "../../utils/appAnalytics";
import "./Home.css";

const PAYMENT_TYPES = [
  { value: "cash", label: "Cash" },
  { value: "cash_box", label: "Cash Box / Honor Box" },
  { value: "cash_app", label: "Cash App" },
  { value: "venmo", label: "Venmo" },
  { value: "paypal", label: "PayPal" },
  { value: "other", label: "Other" },
];

const DIGITAL_PAYMENT_TYPES = new Set(["cash_app", "venmo", "paypal"]);
const QUICK_START_DISMISSED_KEY = "mfp_quick_start_dismissed";

const getPaymentTypeLabel = (value) =>
  PAYMENT_TYPES.find((type) => type.value === value)?.label || value || "Other";

const DASHBOARD_SUBTITLES = [
  "Here's what's happening on your farm and in your community.",
  "Ready to update today's stand details?",
  "Let's keep your farm moving.",
  "Your local farm hub is ready.",
];

const SHOPPER_DASHBOARD_SUBTITLES = [
  "Find local food, handmade goods, farm stands, and community updates near you.",
  "Browse nearby listings, pickup spots, and local farm connections.",
  "See what's fresh locally and connect with nearby sellers.",
];

const LANDOWNER_DASHBOARD_SUBTITLES = [
  "Post work, find local help, and stay connected with nearby farm people.",
  "Keep local jobs, land access, and community updates close at hand.",
];

const WORKER_DASHBOARD_SUBTITLES = [
  "Find local farm work, complete your profile, and connect with nearby people.",
  "Browse current jobs and keep your local work profile ready.",
];

const FRESH_TODAY_CHIPS = [
  "Eggs",
  "Tomatoes",
  "Sweet Corn",
  "Honey",
  "Flowers",
  "Plants",
  "Beef",
  "Pork",
  "Goat Milk",
  "Jam",
  "Bread",
  "Herbs",
  "Zucchini",
  "Other",
];

const getTimeGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
};

const getRoleKey = (profile = {}) => {
  const role = String(profile?.role || profile?.type || "").toLowerCase();
  if (["community", "buyer", "shopper", "local_buyer", "customer"].includes(role)) return "community";
  if (role === "landowner") return "landowner";
  if (["day_worker", "worker"].includes(role)) return "worker";
  return "seller";
};

const hasBroadLocation = (profile = {}) =>
  Boolean(profile?.city || profile?.county || profile?.state || profile?.locationText || profile?.location);

const getDashboardSubtitle = (roleKey) => {
  const day = new Date().getDate();
  if (roleKey === "community") return SHOPPER_DASHBOARD_SUBTITLES[day % SHOPPER_DASHBOARD_SUBTITLES.length];
  if (roleKey === "landowner") return LANDOWNER_DASHBOARD_SUBTITLES[day % LANDOWNER_DASHBOARD_SUBTITLES.length];
  if (roleKey === "worker") return WORKER_DASHBOARD_SUBTITLES[day % WORKER_DASHBOARD_SUBTITLES.length];
  return DASHBOARD_SUBTITLES[day % DASHBOARD_SUBTITLES.length];
};

const getDashboardActions = (roleKey, profile = {}) => {
  if (roleKey === "community") {
    return [
      { label: "Browse Local Listings", to: "/home", state: { dashboardView: "market" }, primary: true },
      { label: "Find Farm Stands", to: "/home" },
      { label: hasBroadLocation(profile) ? "Complete Profile" : "Set Location", to: "/edit-profile" },
      { label: "View Community", to: "/community" },
    ];
  }

  if (roleKey === "landowner") {
    return [
      { label: "Post a Job", to: "/jobs", primary: true },
      { label: "View Jobs", to: "/jobs" },
      { label: "Browse Workers", to: "/browse-users" },
      { label: "Community", to: "/community" },
    ];
  }

  if (roleKey === "worker") {
    return [
      { label: "View Jobs", to: "/jobs", primary: true },
      { label: "Complete Profile", to: "/edit-profile" },
      { label: "Community", to: "/community" },
    ];
  }

  return [
    { label: "Open My Farm", to: "/my-farm", primary: true },
    { label: "Add New Item", to: "/add-product", primary: true },
    { label: "Add Farm Record", to: "/farm-records" },
    { label: "View Jobs", to: "/jobs" },
  ];
};

const getQuickStartIntro = (roleKey) => {
  if (roleKey === "community") {
    return "Start with your area and a simple profile so you can find local goods, farm stands, and community updates.";
  }

  if (roleKey === "landowner") {
    return "Start with your profile, then post work or land opportunities when you are ready.";
  }

  if (roleKey === "worker") {
    return "Start with your profile so local farms and landowners know how to connect with you.";
  }

  return "Start with one small step so neighbors can trust your profile, find your goods, or connect with your work.";
};

const asDate = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  if (typeof value?.seconds === "number") return new Date(value.seconds * 1000);
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isToday = (value) => {
  const date = asDate(value);
  if (!date) return false;
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
};

const formatRelativeTime = (value) => {
  const date = asDate(value);
  if (!date) return "Not updated yet";

  const diffMinutes = Math.max(0, Math.round((Date.now() - date.getTime()) / 60000));
  if (diffMinutes < 1) return "Updated just now";
  if (diffMinutes < 60) return `Updated ${diffMinutes} minute${diffMinutes === 1 ? "" : "s"} ago`;

  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) return `Updated ${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;

  return `Updated ${date.toLocaleDateString()}`;
};

const getDisplayName = (profile, user) =>
  profile?.farmName ||
  profile?.businessName ||
  profile?.displayName ||
  profile?.name ||
  user?.displayName ||
  "there";

const getStandName = getHomeStandName;

const isPickupSpot = (stand = {}) => {
  const explicitKind = String(stand.standKind || stand.standType || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  if (["pickup_spot", "pickup", "porch_pickup", "cooler_pickup"].includes(explicitKind)) {
    return true;
  }

  if (["farm_stand", "stand", "roadside_stand"].includes(explicitKind)) {
    return false;
  }

  const text = [
    stand.category,
    stand.standName,
    stand.name,
    stand.description,
    stand.locationText,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return /\b(pickup|pick-up|porch|cooler|booth|market booth|drop[- ]?off)\b/.test(text);
};

const getStandKindLabel = (stand = {}) => (isPickupSpot(stand) ? "Pickup Spot" : "Farm Stand");

const getStandStatus = getHomeStandStatus;

const getStandStatusLabel = (status) =>
  FRESH_FIND_STATUS_OPTIONS.find((option) => option.value === status)?.label || "Closed";

const getStandStatusBadgeLabel = (status) => getStandStatusLabel(status).toUpperCase();

const getStandHours = (stand = {}) => {
  if (stand.standSelfServe24_7 === true) return "Self-serve 24/7";
  if (stand.standOpenTime && stand.standCloseTime) {
    return `${stand.standOpenTime} - ${stand.standCloseTime}`;
  }
  if (stand.hours) return stand.hours;
  if (stand.businessHours) return stand.businessHours;
  return "";
};

const getStandAvailableItemCount = (stand = {}) => {
  const directCount = Number(
    stand.availableItemCount ??
      stand.featuredItemCount ??
      stand.itemCount ??
      stand.freshTodayCount
  );
  if (Number.isFinite(directCount) && directCount > 0) return directCount;
  if (Array.isArray(stand.freshTodayTags)) return stand.freshTodayTags.length;
  if (Array.isArray(stand.featuredItems)) return stand.featuredItems.length;
  return 0;
};

const getQuantity = (item = {}) =>
  Number(item.quantityAvailable ?? item.quantity ?? item.availableQuantity ?? 0);

const getProductOwnerId = (item = {}) => item.ownerId || item.sellerId || item.userId || "";

const isActiveProduct = (item = {}) =>
  !item.archived &&
  !item.deleted &&
  !item.hidden &&
  item.isArchived !== true &&
  item.status !== "deleted" &&
  item.status !== "archived";

const productMatchesStand = (product = {}, stand = {}) => {
  const standId = stand.id;
  if (!standId) return false;

  return [
    product.standId,
    product.farmStandId,
    product.pickupSpotId,
    product.pickupLocationId,
    product.linkedStandId,
    product.linkedPickupSpotId,
  ].some((value) => value === standId);
};

const productIsShelfEligible = (product = {}, stand = {}) => {
  if (!isActiveProduct(product)) return false;

  const ownerId = stand.ownerId || stand.userId || "";
  if (ownerId && getProductOwnerId(product) !== ownerId) return false;

  return true;
};

const deDupeById = (items = []) => {
  const seen = new Set();
  return items.filter((item) => {
    if (!item.id || seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
};

const getProductName = (item = {}) => item.name || item.title || item.productName || "Untitled item";

const getProductStatusBadge = (item = {}) => {
  const quantity = getQuantity(item);
  const status = getProductAvailabilityStatus(item);
  if (quantity <= 0 || status === "out_of_stock") return "Sold Out";
  if (status === "made_to_order") return "Made To Order";
  if (quantity <= 2) return "Low Stock";
  return "Available";
};

const StandPreviewCard = ({ stand, compact = false, shelfItems = [] }) => (
  <article className="dashboard-preview-card stand-preview-card">
    <div className="dashboard-preview-image">
      {stand.standImage ? (
        <img src={stand.standImage} alt={getStandName(stand)} loading="lazy" />
      ) : (
        <span>{getStandKindLabel(stand)}</span>
      )}
    </div>
    <div className="dashboard-preview-body">
      <p className="stand-preview-eyebrow">Local farm</p>
      <h3>{getStandName(stand)}</h3>
      <div className="dashboard-preview-labels">
        <span>{getStandKindLabel(stand)}</span>
        <span className={`fresh-status fresh-status-${stand.freshStatus}`}>
          {getStandStatusBadgeLabel(stand.freshStatus)}
        </span>
      </div>
      <p>{getHomeStandLocationLabel(stand)}{stand.distance != null ? ` - ${stand.distance.toFixed(1)} mi` : ""}</p>
      {!compact && <p className="preview-note">{stand.freshTodayNote || stand.description || "Check current local pickup details."}</p>}
      {!compact && (
        <div className="live-shelf-preview" aria-label="Live shelf preview">
          {shelfItems.length > 0 ? (
            shelfItems.slice(0, 3).map((item) => (
              <span key={item.id}>
                <strong>{getProductName(item)}</strong>
                <small>
                  {formatProductPrice(item)}
                  {getQuantity(item) != null ? ` - ${getQuantity(item)} available` : ""}
                </small>
              </span>
            ))
          ) : (
            <span>No shelf items posted yet.</span>
          )}
        </div>
      )}
      {getStandAvailableItemCount(stand) > 0 && (
        <p className="preview-count">
          {getStandAvailableItemCount(stand)} featured item{getStandAvailableItemCount(stand) === 1 ? "" : "s"} available
        </p>
      )}
      <small>{formatRelativeTime(stand.freshFindsUpdatedAt || stand.updatedAt)}</small>
      <div className="preview-card-actions">
        <Link to={`/stand/${stand.id}`}>
          {isPickupSpot(stand) ? "View Pickup Spot" : "View Live Shelf"}
        </Link>
        {standCanShowHomeDirections(stand) && getStandDirectionsUrl(stand) && (
          <a
            href={getStandDirectionsUrl(stand)}
            target="_blank"
            rel="noreferrer"
          >
            Directions
          </a>
        )}
      </div>
    </div>
  </article>
);

const Home = ({ currentUser, userLocation, userProfile }) => {
  const location = useLocation();
  const requestedDashboardView = location.state?.dashboardView;

  const [farmStands, setFarmStands] = useState([]);
  const [myProducts, setMyProducts] = useState([]);
  const [standProducts, setStandProducts] = useState([]);
  const [myJobPosts, setMyJobPosts] = useState([]);
  const [myCommunityPosts, setMyCommunityPosts] = useState([]);
  const [sales, setSales] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [standForm, setStandForm] = useState({
    standId: "",
    freshFindsStatus: "closed",
    freshTodayNote: "",
    freshTodayTags: [],
    showInLocalFreshFinds: false,
    standOpenTime: "",
    standCloseTime: "",
    standSelfServe24_7: false,
    standWelcomeMessage: "",
  });
  const [saleForm, setSaleForm] = useState({
    productId: "",
    itemName: "",
    quantity: "1",
    price: "",
    paymentType: "cash",
    note: "",
  });
  const [standSaving, setStandSaving] = useState(false);
  const [standStatusError, setStandStatusError] = useState("");
  const [saleSaving, setSaleSaving] = useState(false);
  const [savingInventoryId, setSavingInventoryId] = useState("");
  const [inventoryError, setInventoryError] = useState("");
  const [quickStartDismissed, setQuickStartDismissed] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(QUICK_START_DISMISSED_KEY) === "true";
  });

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "farmStands"), (snapshot) => {
      setFarmStands(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "products"),
      (snapshot) => setStandProducts(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))),
      () => setStandProducts([])
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!currentUser?.uid) return undefined;

    const ownerQueries = ["ownerId", "userId", "sellerId"].map((field) =>
      onSnapshot(
        query(collection(db, "products"), where(field, "==", currentUser.uid)),
        (snapshot) => {
          setMyProducts((current) =>
            deDupeById([
              ...current.filter((item) => item[field] !== currentUser.uid),
              ...snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })),
            ])
          );
        }
      )
    );

    return () => ownerQueries.forEach((unsub) => unsub());
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!currentUser?.uid) return undefined;

    const jobUnsubs = ["fields", "dayJobs"].map((collectionName) =>
      onSnapshot(
        query(collection(db, collectionName), where("ownerId", "==", currentUser.uid)),
        (snapshot) => {
          setMyJobPosts((current) =>
            deDupeById([
              ...current.filter((item) => item.collectionName !== collectionName),
              ...snapshot.docs.map((docSnap) => ({
                id: `${collectionName}-${docSnap.id}`,
                docId: docSnap.id,
                collectionName,
                ...docSnap.data(),
              })),
            ])
          );
        },
        () => setMyJobPosts((current) => current.filter((item) => item.collectionName !== collectionName))
      )
    );

    return () => jobUnsubs.forEach((unsub) => unsub());
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!currentUser?.uid) return undefined;

    const unsub = onSnapshot(
      query(collection(db, "communityPosts"), where("authorId", "==", currentUser.uid)),
      (snapshot) => {
        setMyCommunityPosts(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      },
      () => setMyCommunityPosts([])
    );

    return () => unsub();
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!currentUser?.uid) return undefined;

    const unsub = onSnapshot(
      query(collection(db, "farmStandSales"), where("ownerId", "==", currentUser.uid)),
      (snapshot) => {
        setSales(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      }
    );

    return () => unsub();
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!currentUser?.uid) return undefined;

    const unsub = onSnapshot(
      query(collection(db, "farmTasks"), where("ownerId", "==", currentUser.uid)),
      (snapshot) => {
        setTasks(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      },
      () => setTasks([])
    );

    return () => unsub();
  }, [currentUser?.uid]);

  const myFarmStands = useMemo(
    () =>
      farmStands.filter(
        (stand) =>
          (stand.ownerId === currentUser?.uid || stand.userId === currentUser?.uid) &&
          stand.isArchived !== true &&
          stand.archived !== true
      ),
    [farmStands, currentUser?.uid]
  );

  const primaryStand = myFarmStands[0] || null;

  const profileCompletion = useMemo(
    () =>
      calculateProfileCompletion({
        profile: userProfile || {},
        products: myProducts,
        farmStands: myFarmStands,
        jobs: myJobPosts,
        communityPosts: myCommunityPosts,
      }),
    [myCommunityPosts, myFarmStands, myJobPosts, myProducts, userProfile]
  );
  const roleKey = getRoleKey(userProfile || {});

  const quickStartSteps = useMemo(() => {
    const role = String(userProfile?.role || userProfile?.type || "").toLowerCase();
    const base = [
      {
        id: "profile",
        label: "Complete your profile",
        to: "/edit-profile",
        complete: profileCompletion.percent >= 80,
      },
    ];

    if (role === "landowner") {
      return [
        ...base,
        { id: "field_job", label: "Post a field job", to: "/map", complete: myJobPosts.length > 0 },
        { id: "day_job", label: "Post a day job", to: "/jobs", complete: myJobPosts.length > 0 },
        { id: "workers", label: "Browse local workers", to: "/browse-users", complete: false },
        { id: "community", label: "Introduce yourself", to: "/community", complete: myCommunityPosts.length > 0 },
      ];
    }

    if (roleKey === "worker") {
      return [
        ...base,
        { id: "jobs", label: "Browse jobs", to: "/jobs", complete: false },
        { id: "notifications", label: "Turn on notifications", to: "/edit-profile", complete: false },
        { id: "community", label: "Introduce yourself", to: "/community", complete: myCommunityPosts.length > 0 },
      ];
    }

    if (roleKey === "community") {
      return [
        {
          id: "location",
          label: "Add broad location",
          to: "/edit-profile",
          complete: hasBroadLocation(userProfile || {}),
        },
        ...base,
        { id: "browse", label: "Browse local listings", to: "/home", state: { dashboardView: "market" }, complete: false },
        { id: "stands", label: "Browse farm stands", to: "/home", complete: false },
        { id: "community", label: "Introduce yourself", to: "/community", complete: myCommunityPosts.length > 0 },
      ];
    }

    return [
      ...base,
      { id: "product", label: "Add your first product", to: "/add-product", complete: myProducts.length > 0 },
      { id: "stand", label: "Add a farm stand / pickup spot", to: "/add-farm-stand", complete: myFarmStands.length > 0 },
      { id: "records", label: "Open Farm Records", to: "/farm-records", complete: false },
      { id: "community", label: "Introduce yourself", to: "/community", complete: myCommunityPosts.length > 0 },
    ];
  }, [myCommunityPosts.length, myFarmStands.length, myJobPosts.length, myProducts.length, profileCompletion.percent, roleKey, userProfile]);

  const showQuickStart = !quickStartDismissed && profileCompletion.percent < 100;
  const firstRecommendedStep =
    quickStartSteps.find((step) => !step.complete) || quickStartSteps[0];
  const profileChecklistActions = {
    photo: { to: "/edit-profile", action: "Add photo" },
    name: { to: "/edit-profile", action: "Add name" },
    location: { to: "/edit-profile", action: "Set location" },
    bio: { to: "/edit-profile", action: "Add bio" },
    role: { to: "/edit-profile", action: "Choose role" },
    browse: { to: "/home", action: "Browse" },
    stands: { to: "/home", action: "Find stands" },
    first_action: { to: "/community", action: "Start posting" },
    first_product: { to: "/add-product", action: "Add listing" },
    first_job: { to: "/jobs", action: "Post job" },
    worker_intro: { to: "/community", action: "Introduce yourself" },
    community_intro: { to: "/community", action: "Introduce yourself" },
  };

  useEffect(() => {
    if (!currentUser?.uid || !showQuickStart) return;
    trackAppEvent("onboarding_started", {
      feature: "quick_start",
      action: "show",
      role: userProfile?.role || userProfile?.type || "",
      completionPercent: profileCompletion.percent,
    });
  }, [currentUser?.uid, profileCompletion.percent, showQuickStart, userProfile?.role, userProfile?.type]);

  const handleQuickStartDismiss = () => {
    setQuickStartDismissed(true);
    try {
      window.localStorage.setItem(QUICK_START_DISMISSED_KEY, "true");
    } catch (error) {}
    trackAppEvent("onboarding_completed", {
      feature: "quick_start",
      action: "dismiss",
      completionPercent: profileCompletion.percent,
    });
  };

  const handleQuickStartStep = (step) => {
    trackAppEvent("onboarding_step_completed", {
      feature: "quick_start",
      action: "open_step",
      step: step.id,
      completionPercent: profileCompletion.percent,
    });
  };

  useEffect(() => {
    if (!primaryStand) return;
    setStandForm({
      standId: primaryStand.id,
      freshFindsStatus: getStandStatus(primaryStand),
      freshTodayNote: primaryStand.freshTodayNote || "",
      freshTodayTags: Array.isArray(primaryStand.freshTodayTags) ? primaryStand.freshTodayTags : [],
      showInLocalFreshFinds: isHomeFreshFindsOptedIn(primaryStand),
      standOpenTime: primaryStand.standOpenTime || "",
      standCloseTime: primaryStand.standCloseTime || "",
      standSelfServe24_7: primaryStand.standSelfServe24_7 === true,
      standWelcomeMessage: primaryStand.standWelcomeMessage || "",
    });
  }, [
    primaryStand?.id,
    primaryStand?.freshFindsStatus,
    primaryStand?.freshTodayNote,
    primaryStand?.freshTodayTags,
    primaryStand?.showInLocalFreshFinds,
    primaryStand?.standOpenTime,
    primaryStand?.standCloseTime,
    primaryStand?.standSelfServe24_7,
    primaryStand?.standWelcomeMessage,
  ]);

  const localFreshFinds = useMemo(() => {
    return prepareHomeFarmStandDiscoveryItems(
      farmStands,
      userLocation || userProfile || {}
    ).slice(0, 6);
  }, [farmStands, userLocation, userProfile]);

  const pickupSpots = useMemo(() => {
    const viewerLocation = normalizeLocationRecord(userLocation || userProfile || {});

    return farmStands
      .filter(
        (stand) =>
          stand.isActive !== false &&
          isHomePickupSpotDiscoverable(stand) &&
          isPickupSpot(stand) &&
          stand.isArchived !== true &&
          stand.archived !== true &&
          stand.deleted !== true
      )
      .map((stand) => {
        const standLocation = normalizeLocationRecord(stand);
        return {
          ...stand,
          freshStatus: getStandStatus(stand),
          distance: getSafeDistanceMiles(viewerLocation, standLocation),
        };
      })
      .sort((a, b) => {
        if (a.distance != null && b.distance != null) return a.distance - b.distance;
        if (a.distance != null) return -1;
        if (b.distance != null) return 1;
        return getStandName(a).localeCompare(getStandName(b));
      })
      .slice(0, 10);
  }, [farmStands, userLocation, userProfile]);

  const activeProducts = useMemo(
    () => deDupeById(myProducts).filter((item) => isActiveProduct(item)),
    [myProducts]
  );

  const getShelfItemsForStand = (stand, options = {}) => {
    const ownerItems = deDupeById(standProducts).filter((product) =>
      productIsShelfEligible(product, stand)
    );
    const linkedItems = ownerItems.filter((product) => productMatchesStand(product, stand));
    const shelfItems = linkedItems.length > 0 ? linkedItems : ownerItems;

    return shelfItems
      .filter((product) => options.includeSoldOut || getQuantity(product) > 0)
      .sort((a, b) => (asDate(b.updatedAt || b.createdAt)?.getTime() || 0) - (asDate(a.updatedAt || a.createdAt)?.getTime() || 0));
  };

  const todaySales = useMemo(
    () =>
      sales
        .filter((sale) => isToday(sale.createdAt))
        .sort((a, b) => (asDate(b.createdAt)?.getTime() || 0) - (asDate(a.createdAt)?.getTime() || 0)),
    [sales]
  );

  const dailyTotal = todaySales.reduce(
    (total, sale) => total + Number(sale.quantity || 0) * Number(sale.price || 0),
    0
  );
  const cashTotal = todaySales
    .filter((sale) => sale.paymentCategory === "cash" || ["cash", "cash_box"].includes(sale.paymentType))
    .reduce((total, sale) => total + Number(sale.totalAmount ?? Number(sale.quantity || 0) * Number(sale.price || 0)), 0);
  const digitalTotal = todaySales
    .filter((sale) => sale.paymentCategory === "digital" || DIGITAL_PAYMENT_TYPES.has(sale.paymentType))
    .reduce((total, sale) => total + Number(sale.totalAmount ?? Number(sale.quantity || 0) * Number(sale.price || 0)), 0);
  const otherTotal = Math.max(0, dailyTotal - cashTotal - digitalTotal);
  const itemsSold = todaySales.reduce((total, sale) => total + Number(sale.quantity || 0), 0);
  const averageSale = todaySales.length ? dailyTotal / todaySales.length : 0;
  const lowStockItems = activeProducts.filter((product) => getQuantity(product) <= 2).length;
  const profileReadiness = getStorefrontReadiness({ profile: userProfile || {}, products: activeProducts });
  const publicStorefrontUrl = getStorefrontUrl(userProfile || {});
  const dashboardSubtitle = getDashboardSubtitle(roleKey);
  const dashboardActions = getDashboardActions(roleKey, userProfile || {});
  const quickStartIntro = getQuickStartIntro(roleKey);
  const freshFindUpdatedAt = asDate(primaryStand?.freshFindsUpdatedAt || primaryStand?.updatedAt);
  const freshFindIsStale =
    primaryStand &&
    (!freshFindUpdatedAt || Date.now() - freshFindUpdatedAt.getTime() > 24 * 60 * 60 * 1000);
  const billySuggestions = [
    tasks.filter((task) => task.status !== "completed").length === 0 && "No reminders today.",
    !primaryStand && activeProducts.length > 0 && "You have listings but no stand / pickup spot yet.",
    freshFindIsStale && "Your Fresh Today note has not been updated recently.",
    !profileReadiness.ready && "Complete your profile to help buyers trust your business.",
    lowStockItems > 0 && "You have low stock items.",
    activeProducts.length === 0 && "You have no active listings yet.",
  ].filter(Boolean);

  if (requestedDashboardView) {
    return (
      <JobFeed
        currentUser={currentUser}
        userLocation={userLocation}
        userProfile={userProfile}
        initialDashboardView={requestedDashboardView}
      />
    );
  }

  const handleStandFieldChange = (field, value) => {
    setStandForm((current) => ({ ...current, [field]: value }));
  };

  const toggleFreshTodayChip = (chip) => {
    setStandForm((current) => {
      const note = current.freshTodayNote || "";
      const parts = note
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean);
      const currentTags = Array.isArray(current.freshTodayTags) ? current.freshTodayTags : [];
      const exists = currentTags.some((part) => part.toLowerCase() === chip.toLowerCase());
      const nextTags = exists
        ? currentTags.filter((part) => part.toLowerCase() !== chip.toLowerCase())
        : [...currentTags, chip];
      const noteHasChip = parts.some((part) => part.toLowerCase() === chip.toLowerCase());
      const nextParts = exists
        ? parts.filter((part) => part.toLowerCase() !== chip.toLowerCase())
        : noteHasChip
          ? parts
          : [...parts, chip];

      return {
        ...current,
        freshTodayTags: nextTags,
        freshTodayNote: nextParts.join(", "),
      };
    });
  };

  const buildInventoryPayload = (item, nextQuantity) => {
    const safeQuantity = Math.max(0, Number(nextQuantity) || 0);
    const nextPayload = {
      quantity: safeQuantity,
      quantityAvailable: safeQuantity,
      availableQuantity: safeQuantity,
      outOfStock: safeQuantity <= 0,
      availabilityStatus: safeQuantity <= 0 ? "out_of_stock" : "available",
    };

    if (safeQuantity > 0 && ["out_of_stock", "sold_out"].includes(String(item.status || "").toLowerCase())) {
      nextPayload.status = "active";
    }

    return nextPayload;
  };

  const quickUpdateProduct = async (item, payload) => {
    if (!item?.id || savingInventoryId === item.id) return;

    try {
      setInventoryError("");
      setSavingInventoryId(item.id);
      await updateDoc(doc(db, "products", item.id), {
        ...payload,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Failed updating listing:", error);
      setInventoryError("Could not update that listing. Please try again.");
    } finally {
      setSavingInventoryId("");
    }
  };

  const changeProductQuantity = (item, delta) => {
    const nextQuantity = getQuantity(item) + delta;
    quickUpdateProduct(item, buildInventoryPayload(item, nextQuantity));
  };

  const markProductSoldOut = (item) => {
    quickUpdateProduct(item, buildInventoryPayload(item, 0));
  };

  const saveStandStatus = async (event) => {
    event.preventDefault();
    if (!standForm.standId) return;

    try {
      setStandStatusError("");
      setStandSaving(true);
      await updateDoc(doc(db, "farmStands", standForm.standId), {
        freshFindsStatus: standForm.freshFindsStatus,
        serviceAvailabilityStatus: standForm.freshFindsStatus,
        freshTodayNote: standForm.freshTodayNote.trim().slice(0, 220),
        freshTodayTags: Array.isArray(standForm.freshTodayTags)
          ? standForm.freshTodayTags.slice(0, 20)
          : [],
        freshFindsUpdatedAt: serverTimestamp(),
        statusUpdatedAt: serverTimestamp(),
        showInLocalFreshFinds: standForm.showInLocalFreshFinds,
        standOpenTime: standForm.standOpenTime,
        standCloseTime: standForm.standCloseTime,
        standSelfServe24_7: standForm.standSelfServe24_7 === true,
        standWelcomeMessage: standForm.standWelcomeMessage.trim().slice(0, 160),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Failed updating farm stand dashboard status:", error);
      setStandStatusError("Could not update your stand status. Contact support if this keeps happening.");
    } finally {
      setStandSaving(false);
    }
  };

  const handleProductForSale = (productId) => {
    const product = activeProducts.find((item) => item.id === productId);
    setSaleForm((current) => ({
      ...current,
      productId,
      itemName: product ? getProductName(product) : current.itemName,
      price: product?.price != null ? String(product.price) : current.price,
    }));
  };

  const saveSale = async (event) => {
    event.preventDefault();
    if (!currentUser?.uid) {
      alert("Please log in before logging a sale.");
      return;
    }

    const itemName = saleForm.itemName.trim();
    const quantity = Number(saleForm.quantity || 0);
    const price = Number(saleForm.price || 0);

    if (!itemName || quantity <= 0 || price < 0) {
      alert("Add an item name, quantity, and price before logging a sale.");
      return;
    }

    try {
      setSaleSaving(true);
      await addDoc(collection(db, "farmStandSales"), {
        ownerId: currentUser.uid,
        standId: standForm.standId || primaryStand?.id || "",
        productId: saleForm.productId || "",
        itemName,
        quantity,
        price,
        totalAmount: quantity * price,
        paymentType: saleForm.paymentType,
        paymentCategory:
          saleForm.paymentType === "cash" || saleForm.paymentType === "cash_box"
            ? "cash"
            : DIGITAL_PAYMENT_TYPES.has(saleForm.paymentType)
              ? "digital"
              : "other",
        note: saleForm.note.trim(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setSaleForm({
        productId: "",
        itemName: "",
        quantity: "1",
        price: "",
        paymentType: "cash",
        note: "",
      });
    } catch (error) {
      console.error("Failed logging sale:", error);
      alert("Could not log the sale. Please try again.");
    } finally {
      setSaleSaving(false);
    }
  };

  return (
    <div className="farm-dashboard page-shell page-shell-wide">
      <header className="farm-dashboard-hero">
        <div>
          <p className="eyebrow">Home</p>
          <h1>{getTimeGreeting()}, {getDisplayName(userProfile, currentUser)}!</h1>
          <p>{dashboardSubtitle}</p>
        </div>
        <div className="farm-dashboard-hero-actions">
          <SupportLink className="btn-soft" />
          {dashboardActions.map((action) => (
            <Link
              key={action.label}
              to={action.to}
              state={action.state}
              className={action.primary ? "btn-primary" : "btn-soft"}
            >
              {action.label}
            </Link>
          ))}
        </div>
      </header>

      <CommunityAlertBar
        currentUser={currentUser}
        userLocation={userLocation}
        userProfile={userProfile}
      />

      {showQuickStart && (
        <section className="dashboard-card quick-start-card" aria-label="Quick start">
          <div className="quick-start-header">
            <div>
              <p className="eyebrow">Quick start</p>
              <h2>Welcome to your local farm hub</h2>
              <p>{quickStartIntro}</p>
            </div>
            <button type="button" className="btn-soft" onClick={handleQuickStartDismiss}>
              Minimize
            </button>
          </div>

          {firstRecommendedStep && (
            <div className="quick-start-primary">
              <div>
                <span>Recommended first step</span>
                <strong>{firstRecommendedStep.label}</strong>
              </div>
              <Link
                className="btn-primary"
                to={firstRecommendedStep.to}
                state={firstRecommendedStep.state}
                onClick={() => handleQuickStartStep(firstRecommendedStep)}
              >
                Start Here
              </Link>
            </div>
          )}

          <div className="profile-completion-meter">
            <div>
              <strong>{profileCompletion.percent}% profile ready</strong>
              <span>{profileCompletion.completed} of {profileCompletion.total} items complete</span>
            </div>
            <div className="profile-completion-track" aria-hidden="true">
              <span style={{ width: `${profileCompletion.percent}%` }} />
            </div>
          </div>

          <div className="quick-start-grid">
            {quickStartSteps.map((step) => (
              <Link
                key={step.id}
                to={step.to}
                state={step.state}
                className={`quick-start-step${step.complete ? " is-complete" : ""}`}
                onClick={() => handleQuickStartStep(step)}
              >
                <span aria-hidden="true">{step.complete ? "Done" : "Next"}</span>
                <strong>{step.label}</strong>
              </Link>
            ))}
          </div>

          <div className="profile-checklist-mini">
            {profileCompletion.checklist.map((item) => (
              <div key={item.id} className={item.complete ? "complete" : ""}>
                <span>{item.complete ? "Done:" : "To do:"} {item.label}</span>
                {!item.complete && profileChecklistActions[item.id] && (
                  <Link to={profileChecklistActions[item.id].to}>
                    {profileChecklistActions[item.id].action}
                  </Link>
                )}
              </div>
            ))}
          </div>

          <p className="dashboard-helper support-helper">
            Need help getting set up? <SupportLink>Contact support</SupportLink>
          </p>
        </section>
      )}

      <InstallAppButton
        variant="card"
        source="dashboard_home"
        title="Use MyFarmingPal like an app"
        description="Add it to your phone so local stands, jobs, messages, and records are one tap away."
      />

      <SectionCarousel
        title="Farm Stands Near You"
        subtitle="Open local farm stands with fresh updates near you."
        viewAllTo="/add-farm-stand"
        viewAllLabel="Add Farm Stand"
        emptyTitle="No farm stand updates yet."
        emptyText="Open or self-serve stands with fresh updates will show here."
        className="local-fresh-section"
      >
        {localFreshFinds.map((stand) => (
          <StandPreviewCard
            key={`fresh-stand-${stand.id}`}
            stand={stand}
            shelfItems={getShelfItemsForStand(stand)}
          />
        ))}
      </SectionCarousel>

      <SectionCarousel
        title="Pickup Spots"
        subtitle="Porch pickups, cooler pickups, booths, and local pickup locations."
        viewAllTo="/add-farm-stand"
        viewAllLabel="Add Pickup Spot"
        emptyTitle="No pickup spots nearby yet."
        emptyText="Create a pickup spot when you are ready to share pickup details."
      >
        {pickupSpots.map((stand) => (
          <StandPreviewCard
            key={stand.id}
            stand={stand}
            compact
            shelfItems={getShelfItemsForStand(stand)}
          />
        ))}
      </SectionCarousel>

      <main className="farm-dashboard-layout">
        <div className="farm-dashboard-main">
          {primaryStand && (
            <section className="dashboard-card stand-control-card" id="stand-control">
              <form onSubmit={saveStandStatus}>
                <div className="dashboard-card-heading">
                  <div>
                    <p className="eyebrow">Live status</p>
                    <h2>Today&apos;s Stand / Pickup Spot</h2>
                    <p>{getStandName(primaryStand)} - {formatRelativeTime(primaryStand.freshFindsUpdatedAt || primaryStand.updatedAt)}</p>
                  </div>
                  <span className={`fresh-status fresh-status-${standForm.freshFindsStatus}`}>
                    {getStandStatusBadgeLabel(standForm.freshFindsStatus)}
                  </span>
                </div>

                <div className="stand-home-summary">
                  <p>{standForm.freshTodayNote || primaryStand.description || "Add a fresh-today note when you are ready to update buyers."}</p>
                  <small>{getStandHours(primaryStand) || "Hours not listed yet"}</small>
                </div>

                <details className="dashboard-collapsible manage-stand-panel">
                  <summary>Manage this stand</summary>

                  <details className="dashboard-collapsible nested">
                    <summary>Live Status</summary>
                    <div className="fresh-status-control" role="group" aria-label="Stand or pickup spot live status">
                      {FRESH_FIND_STATUS_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          className={standForm.freshFindsStatus === option.value ? "active" : ""}
                          onClick={() => handleStandFieldChange("freshFindsStatus", option.value)}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>

                    <label className="dashboard-field">
                      Fresh Today
                      <textarea
                        value={standForm.freshTodayNote}
                        onChange={(event) => handleStandFieldChange("freshTodayNote", event.target.value)}
                        maxLength={220}
                        placeholder="Example: Eggs, tomatoes, sourdough, and self-serve flowers until dusk."
                      />
                    </label>

                    <div className="fresh-chip-grid" aria-label="Fresh today quick chips">
                      {FRESH_TODAY_CHIPS.map((chip) => {
                        const active =
                          (Array.isArray(standForm.freshTodayTags) &&
                            standForm.freshTodayTags.some((tag) => tag.toLowerCase() === chip.toLowerCase())) ||
                          standForm.freshTodayNote.toLowerCase().includes(chip.toLowerCase());
                        return (
                          <button
                            key={chip}
                            type="button"
                            className={active ? "active" : ""}
                            onClick={() => toggleFreshTodayChip(chip)}
                          >
                            {chip}
                          </button>
                        );
                      })}
                    </div>
                  </details>

                  <details className="dashboard-collapsible nested">
                    <summary>Open/close hours</summary>
                    <div className="stand-hours-grid">
                      <label className="dashboard-field">
                        Open time
                        <input
                          type="time"
                          value={standForm.standOpenTime}
                          onChange={(event) => handleStandFieldChange("standOpenTime", event.target.value)}
                          disabled={standForm.standSelfServe24_7}
                        />
                      </label>
                      <label className="dashboard-field">
                        Close time
                        <input
                          type="time"
                          value={standForm.standCloseTime}
                          onChange={(event) => handleStandFieldChange("standCloseTime", event.target.value)}
                          disabled={standForm.standSelfServe24_7}
                        />
                      </label>
                      <label className="dashboard-check stand-hours-check">
                        <input
                          type="checkbox"
                          checked={standForm.standSelfServe24_7}
                          onChange={(event) => handleStandFieldChange("standSelfServe24_7", event.target.checked)}
                        />
                        24/7 self-serve
                      </label>
                    </div>
                  </details>

                  <details className="dashboard-collapsible nested">
                    <summary>Stand details</summary>
                    <label className="dashboard-field">
                      Welcome message
                      <input
                        value={standForm.standWelcomeMessage}
                        onChange={(event) => handleStandFieldChange("standWelcomeMessage", event.target.value)}
                        maxLength={160}
                        placeholder="Example: Pull in by the white barn. Cash box is on the porch."
                      />
                    </label>

                    <label className="dashboard-check">
                      <input
                        type="checkbox"
                        checked={standForm.showInLocalFreshFinds}
                        onChange={(event) => handleStandFieldChange("showInLocalFreshFinds", event.target.checked)}
                      />
                      Show this stand / pickup spot on the dashboard.
                    </label>
                    <p className="dashboard-helper">
                      Privacy first: you choose whether to appear here. Exact directions only show if your stand / pickup spot is configured to share them publicly.
                    </p>
                    <div className="dashboard-inline-actions">
                      <Link to={`/add-farm-stand?edit=${primaryStand.id}`} className="btn-soft">Edit Stand / Pickup</Link>
                      <Link
                        to={
                          publicStorefrontUrl ||
                          getProfilePath({ id: currentUser?.uid, uid: currentUser?.uid, ...(userProfile || {}) })
                        }
                        className="btn-soft"
                      >
                        View Public Page
                      </Link>
                    </div>
                  </details>

                  <details className="dashboard-collapsible nested">
                    <summary>Add/manage stand items</summary>
                    <div className="dashboard-inline-actions">
                      <Link to="/add-product" className="btn-primary">Add Item</Link>
                      <Link to={getProfilePath({ id: currentUser?.uid, uid: currentUser?.uid, ...(userProfile || {}) })} className="btn-soft">
                        Manage Listings
                      </Link>
                    </div>
                  </details>

                  <details className="dashboard-collapsible nested">
                    <summary>Listing/sale form</summary>
                    <p className="dashboard-helper">Use the compact sales panel below to log a sale without leaving the dashboard.</p>
                    <a href="#today-sales" className="btn-soft">Open Sales Panel</a>
                  </details>

                  <button type="submit" className="btn-primary" disabled={standSaving}>
                    {standSaving ? "Updating..." : "Update Stand"}
                  </button>
                  {standStatusError && (
                    <p className="dashboard-error" role="alert">
                      {standStatusError} <SupportLink>Help / Contact Support</SupportLink>
                    </p>
                  )}
                </details>
              </form>
            </section>
          )}

          {primaryStand && (
            <>
          <section className="dashboard-card inventory-card">
            <details className="dashboard-collapsible inventory-collapse">
              <summary>
                <span>
                  <span className="eyebrow">Stock check</span>
                  <strong>Inventory Overview</strong>
                  <small>{activeProducts.length} active listing{activeProducts.length === 1 ? "" : "s"}</small>
                </span>
              </summary>
              {activeProducts.length === 0 ? (
                <div className="dashboard-empty compact">
                  <strong>No active listings yet.</strong>
                  <p>Add your first product so buyers can find what you have available.</p>
                  <Link to="/add-product">Add listing</Link>
                </div>
              ) : (
                <>
                  {inventoryError && <p className="dashboard-error">{inventoryError}</p>}
                  <div className="inventory-compact-grid">
                    {activeProducts.slice(0, 4).map((item) => {
                      const availabilityStatus = getProductAvailabilityStatus(item);
                      const statusBadge = getProductStatusBadge(item);
                      const isSavingItem = savingInventoryId === item.id;
                      return (
                        <article key={item.id} className="inventory-mini-card">
                          <div>
                            <strong>{getProductName(item)}</strong>
                            <span className={`inventory-badge inventory-badge-${statusBadge.toLowerCase().replace(/\s+/g, "-")}`}>
                              {statusBadge}
                            </span>
                            <span>{getProductAvailabilityLabel(availabilityStatus)}</span>
                          </div>
                          <p>{formatAvailability(getQuantity(item), item.unit || item.sellBy || "")}</p>
                          <p>{formatProductPrice(item)}</p>
                          <div className="inventory-actions">
                            <Link
                              to="/home"
                              state={{ dashboardView: "market", focusProductId: item.id }}
                            >
                              View
                            </Link>
                            <Link to={`/add-product?edit=${item.id}`}>Edit</Link>
                            <button
                              type="button"
                              disabled={isSavingItem}
                              onClick={() => changeProductQuantity(item, -1)}
                            >
                              -1
                            </button>
                            <button
                              type="button"
                              disabled={isSavingItem}
                              onClick={() => changeProductQuantity(item, 1)}
                            >
                              +1
                            </button>
                            <button
                              type="button"
                              disabled={isSavingItem}
                              onClick={() => markProductSoldOut(item)}
                            >
                              Sold Out
                            </button>
                            <button
                              type="button"
                              disabled={isSavingItem}
                              onClick={() =>
                                quickUpdateProduct(item, {
                                  archived: true,
                                  isArchived: true,
                                  status: "archived",
                                })
                              }
                            >
                              Remove
                            </button>
                          </div>
                          {isSavingItem && <small className="inventory-saving">Saving...</small>}
                        </article>
                      );
                    })}
                  </div>
                  <div className="dashboard-inline-actions">
                    <Link to="/add-product" className="btn-primary">Add listing</Link>
                    <Link to={getProfilePath({ id: currentUser?.uid, uid: currentUser?.uid, ...(userProfile || {}) })} className="btn-soft">
                      Manage Inventory
                    </Link>
                  </div>
                </>
              )}
            </details>
          </section>

          <section className="dashboard-card sales-card" id="today-sales">
            <div className="dashboard-card-heading">
              <div>
                <p className="eyebrow">Stand / pickup counter</p>
                <h2>Today&apos;s Sales</h2>
              </div>
              <strong>${dailyTotal.toFixed(2)}</strong>
            </div>

            <div className="sales-metric-grid">
              <article>
                <span>Revenue</span>
                <strong>${dailyTotal.toFixed(2)}</strong>
              </article>
              <article>
                <span>Items sold</span>
                <strong>{itemsSold}</strong>
              </article>
              <article>
                <span>Average sale</span>
                <strong>${averageSale.toFixed(2)}</strong>
              </article>
              <article>
                <span>Cash</span>
                <strong>${cashTotal.toFixed(2)}</strong>
              </article>
              <article>
                <span>Digital</span>
                <strong>${digitalTotal.toFixed(2)}</strong>
              </article>
              <article>
                <span>Other</span>
                <strong>${otherTotal.toFixed(2)}</strong>
              </article>
            </div>

            <details className="dashboard-collapsible sales-log-panel">
              <summary>Log a sale</summary>
              <form className="sales-form" onSubmit={saveSale}>
                <label>
                  Listing
                  <select value={saleForm.productId} onChange={(event) => handleProductForSale(event.target.value)}>
                    <option value="">Manual item</option>
                    {activeProducts.map((item) => (
                      <option key={item.id} value={item.id}>{getProductName(item)}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Item name
                  <input
                    value={saleForm.itemName}
                    onChange={(event) => setSaleForm((current) => ({ ...current, itemName: event.target.value }))}
                    placeholder="Eggs, flowers, tomatoes..."
                  />
                </label>
                <label>
                  Qty
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={saleForm.quantity}
                    onChange={(event) => setSaleForm((current) => ({ ...current, quantity: event.target.value }))}
                  />
                </label>
                <label>
                  Price
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={saleForm.price}
                    onChange={(event) => setSaleForm((current) => ({ ...current, price: event.target.value }))}
                  />
                </label>
                <label>
                  Payment
                  <select
                    value={saleForm.paymentType}
                    onChange={(event) => setSaleForm((current) => ({ ...current, paymentType: event.target.value }))}
                  >
                  {PAYMENT_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                  </select>
                </label>
                <label className="sales-note">
                  Note
                  <input
                    value={saleForm.note}
                    onChange={(event) => setSaleForm((current) => ({ ...current, note: event.target.value }))}
                    placeholder="Optional"
                  />
                </label>
                <button type="submit" className="btn-primary" disabled={saleSaving}>
                  {saleSaving ? "Logging..." : "Log Sale"}
                </button>
              </form>
            </details>

            <div className="sales-list">
              {todaySales.length === 0 ? (
                <div className="dashboard-empty compact">No sales logged today.</div>
              ) : (
                todaySales.map((sale) => (
                  <article key={sale.id} className="sale-row">
                    <div>
                      <strong>{sale.itemName}</strong>
                      <span>{sale.quantity} x ${Number(sale.price || 0).toFixed(2)} - {getPaymentTypeLabel(sale.paymentType)}</span>
                    </div>
                    <strong>${(Number(sale.quantity || 0) * Number(sale.price || 0)).toFixed(2)}</strong>
                  </article>
                ))
              )}
            </div>
          </section>
            </>
          )}
        </div>

        <aside className="farm-dashboard-side">
          <section className="dashboard-card ask-billy-card">
            <p className="eyebrow">Need a hand?</p>
            <h2>Ask Billy</h2>
            <p>Billy can help you find the right next step, from listings to records.</p>
            <button type="button" className="btn-soft">Ask Billy</button>
          </section>

          <section className="dashboard-card">
            <p className="eyebrow">Today</p>
            <h2>Tasks</h2>
            {tasks.filter((task) => task.status !== "completed").slice(0, 4).length === 0 ? (
              <p className="side-empty">No open farm tasks found.</p>
            ) : (
              tasks.filter((task) => task.status !== "completed").slice(0, 4).map((task) => (
                <div key={task.id} className="side-list-item">
                  <strong>{task.title || task.taskName || "Farm task"}</strong>
                  <span>{task.dueDate || task.priority || "Open"}</span>
                </div>
              ))
            )}
          </section>

          <section className="dashboard-card">
            <p className="eyebrow">Billy&apos;s reminders</p>
            <h2>Billy&apos;s Suggestions</h2>
            {billySuggestions.length === 0 ? (
              <p className="side-empty">No reminders today.</p>
            ) : (
              billySuggestions.slice(0, 5).map((suggestion) => (
                <div key={suggestion} className="side-list-item">
                  <strong>{suggestion}</strong>
                  <span>Suggested next step</span>
                </div>
              ))
            )}
          </section>

          <section className="dashboard-card">
            <p className="eyebrow">Activity</p>
            <h2>Recent Activity</h2>
            {activeProducts.length === 0 && todaySales.length === 0 ? (
              <p className="side-empty">No recent listing or sales activity yet.</p>
            ) : (
              <>
                {todaySales.slice(0, 2).map((sale) => (
                  <div key={sale.id} className="side-list-item">
                    <strong>Sale logged</strong>
                    <span>{sale.itemName}</span>
                  </div>
                ))}
                {activeProducts.slice(0, 2).map((item) => (
                  <div key={item.id} className="side-list-item">
                    <strong>Listing active</strong>
                    <span>{getProductName(item)}</span>
                  </div>
                ))}
              </>
            )}
          </section>
        </aside>
      </main>
    </div>
  );
};

export default Home;


